import { type ReactNode, useEffect, useRef, useState } from 'react'
import { DEV_DEMO_TOKEN } from '../config'
import { apolloClient } from '../graphql/client'
import {
  ConsumerRequestPotentialsDocument,
  type ConsumerRequestPotentialsMutation,
  type ConsumerRequestPotentialsMutationVariables,
  RequestPotentialStatusDocument,
  type RequestPotentialStatusQuery,
  type RequestPotentialStatusQueryVariables,
  TradeInConfigDocument,
  type TradeInConfigQuery,
  type TradeInConfigQueryVariables,
  useConsumerLoginMutation,
} from '../generated/graphql'
import { useTranslation } from '../localization/useTranslation'
import { type ShopConfig, useAuthStore } from '../store/auth'
import { IconWarn } from '../ui/icons'
import { readBootstrap } from './bootstrap'
import {
  getAccessToken,
  getMultiLogin,
  getStoreCountry,
  getStoreCurrency,
  getStoreLanguage,
  resolveProductSearchEnabled,
} from './embedConfig'
import { logout } from './logout'
import { signInErrorKey } from './signInError'

/**
 * The "items bought online" feed is generated on demand: ask Core to pull the consumer's online
 * orders into Potentials, then poll the returned tokens until the async work finishes (mirrors
 * v1's LoginProvider + PotentialsRequestDialog). Best-effort — capped so login never hangs.
 */
async function generateOrderFeed(shopId: string, consumerId: string, token: string): Promise<void> {
  const auth = { headers: { authorization: token } }
  const req = await apolloClient.mutate<
    ConsumerRequestPotentialsMutation,
    ConsumerRequestPotentialsMutationVariables
  >({
    mutation: ConsumerRequestPotentialsDocument,
    variables: { shopId, consumerId },
    context: auth,
  })
  const tokens = (req.data?.ConsumerRequestPotentials ?? []).filter((t): t is string => Boolean(t))
  if (tokens.length === 0) return

  const start = Date.now()
  while (Date.now() - start < 30_000) {
    const { data } = await apolloClient.query<
      RequestPotentialStatusQuery,
      RequestPotentialStatusQueryVariables
    >({
      query: RequestPotentialStatusDocument,
      variables: { tokens },
      context: auth,
      fetchPolicy: 'no-cache',
    })
    const messages = data?.IntegrationMessage ?? []
    // Complete when every message has a resultTs (an empty list also counts as done, per v1).
    if (messages.every((m) => m?.resultTs)) return
    await new Promise((resolve) => setTimeout(resolve, 1000))
  }
}

/**
 * The protected area. On mount it exchanges the login accessToken for a real Core session
 * (ConsumerLogin), stores it, and only then renders `children` — every call inside is
 * authenticated. Mirrors v1's AppContextProvider + LoginProvider gating, collapsed into one
 * component backed by the Zustand auth store.
 */

export function AuthProvider({ children }: { children: ReactNode }) {
  const status = useAuthStore((s) => s.status)
  const error = useAuthStore((s) => s.error)
  const [login] = useConsumerLoginMutation()
  const started = useRef(false)

  useEffect(() => {
    if (started.current) return // guard StrictMode double-invoke / re-renders
    started.current = true

    const { shopId, accessToken, preview } = readBootstrap()
    const store = useAuthStore.getState()

    if (!shopId) return store.setError('auth.missingShop')
    if (!accessToken) return store.setError('auth.needSignIn')

    // Local-dev bypass: the harness demo token seeds a mock session with no network call,
    // so the UI is developable offline. Any real token runs the real exchange below.
    if (import.meta.env.DEV && accessToken === DEV_DEMO_TOKEN) {
      store.setSession({
        token: 'dev-session',
        consumerId: 'dev-consumer',
        shopId,
        config: { tradeInComments: true, currency: 'DKK', locale: 'da' },
        selfCreated: false,
      })
      return
    }

    // Self-created is true ONLY for the wholesale ("bought elsewhere") login, which flags it via
    // setLoginToken(token, true) before we get here. A storefront-supplied `tradeInConfig.accessToken`
    // is a real consumer, never self-created — so ignore any stale flag persisted from an earlier
    // wholesale login in this browser session (otherwise it wrongly hides the "My trade-ins" menu).
    const selfCreated = !getAccessToken() && store.consumerSelfCreated

    store.setAuthenticating()
    login({
      variables: {
        shopId,
        accessToken,
        preview,
        multiLogin: getMultiLogin(),
        currency: getStoreCurrency(),
        language: getStoreLanguage(),
        countryCode: getStoreCountry(),
      },
    })
      .then(async (res) => {
        const r = res.data?.ConsumerLogin
        if (!r?.token) return store.setError(signInErrorKey(res.errors?.map((e) => e.message)))

        // With the fresh token, load the shop's trade-in config (brandHouseId etc.) before
        // completing the session, so the finder has everything it needs on first render.
        // Config is non-fatal: if it fails, the flow still runs (search just stays disabled).
        let shopConfig: Partial<ShopConfig> = {}
        try {
          const cfg = await apolloClient.query<TradeInConfigQuery, TradeInConfigQueryVariables>({
            query: TradeInConfigDocument,
            variables: { shopId: r.shopId },
            context: { headers: { authorization: r.token } },
            fetchPolicy: 'network-only',
          })
          const c = cfg.data?.TradeInConfig
          shopConfig = {
            brandHouseId: c?.brandHouseId ?? undefined,
            shopCurrency: c?.shopCurrency ?? undefined,
            maximumVoucher: c?.maximumVoucher ?? 0,
            defaultImage: c?.defaultImage ?? undefined,
            productSearchEnabled: resolveProductSearchEnabled(c?.productSearchEnabled),
            productSearchMask: c?.productSearchMask ?? undefined,
            wholeSaleActive: c?.wholeSaleActive ?? false,
            // Drives the "when do you get your voucher" wording (strategy-variant strings).
            voucherStrategy: c?.voucherStrategy,
          }
        } catch (e) {
          console.warn('TradeInConfig fetch failed — finder will be unavailable', e)
        }

        // Generate the "items bought online" feed (async on Core) before rendering, so the
        // order-feed customer lands straight on their past orders. Skipped for a self-created
        // (wholesale / "bought elsewhere") consumer — they have no past orders to import.
        if (r.requestPotentialsSupported && !selfCreated) {
          try {
            await generateOrderFeed(r.shopId, r.consumerId, r.token)
          } catch (e) {
            console.warn('Order-feed generation failed — continuing without it', e)
          }
        }

        store.setSession({
          token: r.token,
          consumerId: r.consumerId,
          shopId: r.shopId,
          config: {
            brandName: r.brandName,
            locale: r.locale,
            currency: r.currency,
            shopCurrency: r.shopCurrency,
            allowedCountry: r.allowedCountry,
            lockEmail: r.lockEmail,
            requestPotentialsSupported: r.requestPotentialsSupported,
            tradeInComments: r.tradeInComments,
            ...shopConfig,
          },
          selfCreated,
        })
      })
      .catch((e: Error) => store.setError(e.message || 'auth.signInFailed'))
  }, [login])

  if (status === 'authenticated') return <>{children}</>
  if (status === 'error') return <AuthError message={error} />
  return <AuthLoading />
}

/**
 * Sign-in can be slow: after ConsumerLogin we also fetch config and poll Core to generate the
 * order feed (up to ~30s). A bare line of text reads as "stalled/crashed", so we show a spinner and,
 * once it crosses 5s, a reassuring second line that we're still working.
 */
const SLOW_AUTH_MS = 5000

function AuthLoading() {
  const { t } = useTranslation()
  const [slow, setSlow] = useState(false)
  useEffect(() => {
    const id = setTimeout(() => setSlow(true), SLOW_AUTH_MS)
    return () => clearTimeout(id)
  }, [])
  return (
    <div className="center-note">
      <div className="auth-loading" role="status" aria-live="polite">
        <span className="spin" aria-hidden />
        <p>{t('auth.signingIn')}</p>
        {slow && <p className="auth-loading-slow">{t('auth.stillWorking')}</p>}
      </div>
    </div>
  )
}

/**
 * Sign-in failed (e.g. a login link whose profile has since been deleted). Explains why and offers a
 * way out: `logout()` drops every stored access token, so the gate falls back to the front page
 * (login) instead of retrying the dead token on every reload.
 */
function AuthError({ message }: { message: string | null }) {
  const { t } = useTranslation()
  return (
    <div className="center-note">
      <div className="config-error auth-error">
        <span className="config-error-icon">
          <IconWarn />
        </span>
        <h1>{t('auth.errorTitle')}</h1>
        {/* `t` returns the raw string for non-keys, so caught error messages still display. */}
        <p>{t(message ?? 'auth.errorGeneric')}</p>
        <button type="button" className="btn btn-primary" onClick={() => void logout()}>
          {t('auth.backToFrontPage')}
        </button>
      </div>
    </div>
  )
}
